import { Router } from '@angular/router';
import { Account, FieldDef } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { Dictionary } from 'app/shared/models';
import { CustomField, CustomFormOptions } from 'app/shared/modules/custom-form/custom-form.types';
import { CustomFormComponent } from 'app/shared/modules/custom-form/custom-form/custom-form.component';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { EMPTY, forkJoin, noop, Observable, of, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { PipelineService } from '../pipeline.service';
import { ProjectsService } from '../projects.service';
import { CreateFormDefinition, Pipeline, Project, Property } from '../projects.types';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DealsWithSamePropertiesComponent } from './components/deals-with-same-properties/deals-with-same-properties.component';
import { AuthService } from 'app/core';
import { Permission } from 'app/core/enums';
import { RelatedToType } from 'app/shared/enums';

const updateCreateFormDefinition = (
  pipelineService: PipelineService,
  pipeline: Pipeline,
  account: Account,
  model: CreateFormDefinition,
): Observable<Pipeline> => {
  return pipelineService.update({ ...pipeline, create_form_definition: JSON.stringify(model) }, Number(account.id));
};

const addDealToPortfolio = (projectService: ProjectsService, project: Project, parentProject: Project): void => {
  const childIds = [...(parentProject.child_ids || []), project.id];
  const options = { ...{ child_ids: childIds }, id: parentProject.id } as Project;

  projectService
    .saveProject(options, parentProject)
    .pipe(switchMap(() => projectService.getProjectById(parentProject.id)))
    .subscribe(noop);
};

const convertDealToPortfolio = (
  project: Project,
  services: { projectService: ProjectsService },
): Observable<Project> => {
  const { projectService } = services;

  return forkJoin(
    project.properties.map((property) =>
      projectService.saveProject(
        {
          title: property.name || property.address_string || property.street || 'Untitled',
          property_type_id: project.property_type_id,
          project_type_id: project.project_type_id,
          investment_type_id: project.investment_type_id,
          pipeline_id: project.pipeline_id,
          template_id: project.template_id,
          property_ids: [String(property.id)],
        },
        null,
        false,
      ),
    ),
  ).pipe(
    switchMap((projects) => {
      const child_ids = projects.map(({ id }) => id);
      const options = { child_ids, id: project?.id } as Project;
      return projectService.saveProject(options, project);
    }),
  );
};

const setupAddDealByDrawer = (
  services: {
    projectService: ProjectsService;
    pipelineService: PipelineService;
    foldersTabService: FoldersTabService;
    layoutService: LayoutService;
    router: Router;
    matDialog?: MatDialog;
    authService: AuthService;
  },
  models: {
    pipeline: Pipeline;
    parentProject?: Project;
    account: Account;
    fieldDefs: FieldDef[];
    data: any;
    projectTemplates: { id: number; name: string }[];
    disableItemSuggestion?: boolean;
    createPortfolio?: boolean;
    extraDealData?: Dictionary<any>;
    successFn?: (project: Project) => void;
  },
): Observable<Pipeline> => {
  const { projectService, pipelineService, foldersTabService, layoutService, router, matDialog, authService } =
    services;
  const {
    pipeline,
    parentProject,
    account,
    fieldDefs,
    data,
    projectTemplates,
    disableItemSuggestion,
    createPortfolio,
    extraDealData,
    successFn,
  } = models;

  const defaultFormFields: CustomField[] = [
    { name: 'title', isRequired: true, isRemovable: false },
    { name: 'pipeline_id', isRequired: true, isRemovable: false },
    { name: 'template_id', isRequired: true, isRemovable: false },
    { name: 'cff_add_properties', isRemovable: true },
  ];
  const fieldExcludeList = ['cff_address'];

  return of(pipeline).pipe(
    switchMap(({ create_form_definition: createFormDefinition }) => {
      if (createFormDefinition) {
        return of(pipeline);
      } else {
        if (!authService.hasPermission(Permission.PIPELINE_MANAGE)) {
          return of({ ...pipeline, create_form_definition: { fields: defaultFormFields } });
        }

        return updateCreateFormDefinition(pipelineService, pipeline, account, { fields: defaultFormFields });
      }
    }),
    tap(
      ({
        create_form_definition: createFormDefinition,
      }: Pipeline & { create_form_definition: CreateFormDefinition }) => {
        // Filter out excluded fields
        const filteredFields = createFormDefinition.fields.filter(({ name }) => !fieldExcludeList.includes(name));

        // Create a set of existing field names for quick lookup
        const existingFieldNames = new Set(filteredFields.map(({ name }) => name));

        // Add default fields that are not already present
        const selectFields = [
          ...createFormDefinition.fields,
          ...defaultFormFields.filter(({ name }) => !existingFieldNames.has(name)),
        ];

        const found = selectFields.find((obj) => {
          return obj.name === 'cff_add_properties';
        });

        if (!found) {
          selectFields.push({ name: 'cff_add_properties' });
        }

        layoutService.openComponentDrawer<CustomFormOptions>(CustomFormComponent, {
          inputs: {
            relatedToType: RelatedToType.Project,
            allowedFields: fieldDefs,
            selectedFields: selectFields,
            dataSources: { template_id: projectTemplates },
            commonData: {
              pipelineId: pipeline.id,
              formFieldData: data,
              disableItemSuggestion: Boolean(disableItemSuggestion),
              callbackData: createPortfolio ? { createPortfolio } : undefined,
            },
            beforeSaveAction: (formValue: Dictionary<any>) => {
              if (matDialog) {
                const addresses = formValue.properties?.map((property) => {
                  return `${property.address['address'] || property.address['street']} ${property.address['city']}, ${
                    property.address['state']
                  } ${property.address['zip'] || property.address['postal_code']}`;
                }) as string[];

                let addressStreamHandler = of([]);

                if (addresses?.length) {
                  addressStreamHandler = zip(
                    forkJoin(
                      addresses.map((address) =>
                        projectService
                          .getProjectsNoPipeline(
                            ['pipeline_id', 'project_stage', 'created_at', 'updated_at'],
                            { location: { addressString: address, within: '0.1mi' } },
                            0,
                            20,
                            'projects.created_at',
                            'desc',
                            '',
                            true,
                          )
                          .pipe(
                            map(
                              (response: { projects: Project[] }) =>
                                response?.projects.map((project) => ({ ...project, matchedBy: address })) as Project[],
                            ),
                          ),
                      ),
                    ),
                  );
                }

                return addressStreamHandler.pipe(
                  map((deals) => deals.reduce((p, c) => p.concat(...c), [] as Project[])),
                  switchMap((similarDeals) => {
                    if (similarDeals?.length) {
                      return matDialog
                        .open(DealsWithSamePropertiesComponent, { data: { similarDeals } })
                        .afterClosed()
                        .pipe(map((result) => result as boolean));
                    }

                    return of(true);
                  }),
                );
              }

              return of(true);
            },
            saveAction: (formValue: Dictionary<any>, callbackData: Dictionary<any>) => {
              let projectData = { ...formValue };

              if (!formValue.pipeline_id) {
                projectData.pipeline_id = pipeline.id;
                projectData.parent_id = parentProject?.id;
              }

              if (extraDealData) {
                projectData = { ...projectData, ...extraDealData };
              }

              const saveProject = parentProject?.id
                ? projectService.saveProject({ ...projectData, parent_id: parentProject.id }, null, false)
                : projectService.saveProject(projectData);

              saveProject
                .pipe(
                  switchMap((project: Project) => projectService.getProjectById(project.id, 'json', true)),
                  switchMap((project: Project) => {
                    const uploadedFile = formValue.file;
                    if (uploadedFile) {
                      foldersTabService.moduleId = project.id;
                      foldersTabService.moduleBaseUrl = 'projects';

                      return foldersTabService
                        .createFile(project.root_folder_resource_id, {
                          folder_resource_id: project.root_folder_resource_id,
                          name: uploadedFile.name,
                        })
                        .pipe(
                          switchMap((fileResource: FileResource) =>
                            foldersTabService.uploadDocument(
                              fileResource.folder_resource_id,
                              fileResource.id,
                              uploadedFile,
                            ),
                          ),
                          switchMap(() => of(project)),
                        );
                    }

                    return of(project);
                  }),
                  switchMap((project: Project) => {
                    const properties: Property[] = formValue.properties;

                    if (properties?.length) {
                      return forkJoin(
                        properties.map((property) => {
                          if (property.address?.id) {
                            return projectService.relatePropertyToProject(property?.address as Property, project);
                          } else {
                            return projectService.addProperty(property, project);
                          }
                        }),
                      ).pipe(
                        switchMap((properties) => {
                          project.properties = properties;
                          return of(project);
                        }),
                      );
                    }

                    return of(project);
                  }),
                  switchMap((project: Project) => {
                    return callbackData?.createPortfolio && project.properties?.length
                      ? convertDealToPortfolio(project, { projectService })
                      : of(project);
                  }),
                  switchMap((projectResult: Project) => {
                    if (successFn) {
                      successFn(projectResult);
                      return EMPTY;
                    }

                    layoutService.closeComponentDrawer();

                    if (parentProject) {
                      return projectService.getProjectById(parentProject.id, 'json', true);
                    } else {
                      router.navigateByUrl(`/projects/${projectResult.pipeline_id}/${projectResult.id}`);
                      return EMPTY;
                    }
                  }),
                )
                .subscribe(noop);
            },
          },
          outputs: {
            onTemplateChanged: (fields: CustomField[]) => {
              updateCreateFormDefinition(pipelineService, pipeline, account, { fields })
                .pipe(
                  tap(
                    (pipelineResult: Pipeline) =>
                      (pipeline.create_form_definition = pipelineResult.create_form_definition),
                  ),
                )
                .subscribe(noop);
            },
            onNavigationByUrlRequested: (url: string) => (parentProject ? undefined : router.navigateByUrl(url)),
          },
        });
      },
    ),
  );
};

export { setupAddDealByDrawer };
